import React, { useState, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, ChevronRight, ChevronDown, List } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";

interface Group {
  id: string;
  name: string;
  parent_id?: string;
}

interface GroupManagerProps {
  groups: Group[];
  selectedGroup: string | null;
  selectedSubgroup: string | null;
  subgroups: Group[];
  onGroupSelect: (groupId: string | null) => void;
  onSubgroupSelect?: (subgroupId: string | null) => void;
  onAddGroup: () => void;
  onAddSubgroup: (parentId: string, name: string) => void;
  onDeleteGroup: (groupId: string) => void;
  onReorderGroups?: (reorderedGroups: Group[]) => void;
  onReorderSubgroups?: (reorderedSubgroups: Group[]) => void;
  onMoveSubgroup?: (subgroupId: string, newParentId: string) => void;
  onConvertGroupToSubgroup?: (groupId: string, newParentId: string) => void;
  onConvertSubgroupToGroup?: (subgroupId: string) => void;
}

const GroupManager: React.FC<GroupManagerProps> = ({
  groups,
  selectedGroup,
  selectedSubgroup,
  subgroups,
  onGroupSelect,
  onSubgroupSelect,
  onAddGroup,
  onAddSubgroup,
  onDeleteGroup,
  onReorderGroups,
  onReorderSubgroups,
  onMoveSubgroup,
  onConvertGroupToSubgroup,
  onConvertSubgroupToGroup,
}) => {
  const { toast } = useToast();
  const [subgroupNames, setSubgroupNames] = useState<Record<string, string>>({});
  const [showSubgroupPopovers, setShowSubgroupPopovers] = useState<Record<string, boolean>>({});
  const [draggedItem, setDraggedItem] = useState<{id: string, type: 'group' | 'subgroup'} | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<string | null>(null);
  const [treeViewMode, setTreeViewMode] = useState(false);
  const [expandedSubgroups, setExpandedSubgroups] = useState<Set<string>>(new Set());
  
  // Touch drag state
  const [touchDragState, setTouchDragState] = useState<{
    isDragging: boolean;
    startY: number;
    startX: number;
    currentY: number;
    currentX: number;
  }>({ isDragging: false, startY: 0, startX: 0, currentY: 0, currentX: 0 });

  const handleDeleteGroup = async (groupId: string) => {
    try {
      onDeleteGroup(groupId);
      toast({
        title: "Group deleted",
        description: "The group has been successfully deleted.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete the group. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCreateSubgroup = async (parentId: string) => {
    const name = subgroupNames[parentId]?.trim();
    if (!name) return;
    
    try {
      await onAddSubgroup(parentId, name);
      setSubgroupNames(prev => ({ ...prev, [parentId]: "" }));
      setShowSubgroupPopovers(prev => ({ ...prev, [parentId]: false }));
      toast({
        title: "Subgroup created",
        description: "Your new subgroup has been created successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create subgroup. Please try again.",
        variant: "destructive",
      });
    }
  };

  // HTML5 Drag and Drop handlers
  const handleDragStart = (e: React.DragEvent, itemId: string, type: 'group' | 'subgroup') => {
    setDraggedItem({ id: itemId, type });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', itemId);
    
    // Add visual feedback
    e.currentTarget.classList.add('opacity-50');
  };

  const handleDragEnd = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('opacity-50');
    setDraggedItem(null);
    setDragOverTarget(null);
  };

  // Touch event handlers for mobile
  const handleTouchStart = (e: React.TouchEvent, itemId: string, type: 'group' | 'subgroup') => {
    const touch = e.touches[0];
    setTouchDragState({
      isDragging: false,
      startY: touch.clientY,
      startX: touch.clientX,
      currentY: touch.clientY,
      currentX: touch.clientX,
    });
    
    // Start drag after a small delay to distinguish from tap
    setTimeout(() => {
      setDraggedItem({ id: itemId, type });
      setTouchDragState(prev => ({ ...prev, isDragging: true }));
      e.currentTarget.classList.add('opacity-50');
    }, 200);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchDragState.isDragging || !draggedItem) return;
    
    e.preventDefault(); // Prevent scrolling
    const touch = e.touches[0];
    setTouchDragState(prev => ({
      ...prev,
      currentY: touch.clientY,
      currentX: touch.clientX,
    }));

    // Find element under touch point
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
    const dropTarget = elementBelow?.closest('[data-drop-target]');
    
    if (dropTarget) {
      const targetId = dropTarget.getAttribute('data-drop-target');
      setDragOverTarget(targetId);
    } else {
      setDragOverTarget(null);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchDragState.isDragging || !draggedItem) {
      setTouchDragState({ isDragging: false, startY: 0, startX: 0, currentY: 0, currentX: 0 });
      return;
    }

    const touch = e.changedTouches[0];
    const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
    const dropTarget = elementBelow?.closest('[data-drop-target]');
    
    if (dropTarget) {
      const targetId = dropTarget.getAttribute('data-drop-target');
      const targetType = dropTarget.getAttribute('data-drop-type') as 'group' | 'subgroup' | 'all-notes';
      const dropIndex = dropTarget.getAttribute('data-drop-index');
      
      if (targetId && targetType) {
        // Create a mock event for the existing drop handler
        const mockEvent = {
          preventDefault: () => {},
        } as React.DragEvent;
        
        handleDrop(mockEvent, targetId, targetType, dropIndex ? parseInt(dropIndex) : undefined);
      }
    }

    // Clean up
    e.currentTarget.classList.remove('opacity-50');
    setDraggedItem(null);
    setDragOverTarget(null);
    setTouchDragState({ isDragging: false, startY: 0, startX: 0, currentY: 0, currentX: 0 });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    setDragOverTarget(targetId);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverTarget(null);
  };

  const handleDrop = (e: React.DragEvent, targetId: string, targetType: 'group' | 'subgroup' | 'all-notes', dropIndex?: number) => {
    e.preventDefault();
    setDragOverTarget(null);
    
    if (!draggedItem) return;

    const { id: draggedId, type: draggedType } = draggedItem;
    
    // Prevent dropping item on itself
    if (draggedId === targetId) return;

    // Handle reordering within the same container
    if (draggedType === 'group' && targetType === 'group' && typeof dropIndex === 'number') {
      // Reorder groups
      const draggedIndex = groups.findIndex(g => g.id === draggedId);
      if (draggedIndex !== -1 && draggedIndex !== dropIndex) {
        const newGroups = [...groups];
        const [draggedGroup] = newGroups.splice(draggedIndex, 1);
        newGroups.splice(dropIndex, 0, draggedGroup);
        onReorderGroups?.(newGroups);
        setDraggedItem(null);
        return;
      }
    }

    if (draggedType === 'subgroup' && targetType === 'subgroup' && typeof dropIndex === 'number') {
      // Reorder subgroups within the same parent
      const draggedSubgroup = subgroups.find(s => s.id === draggedId);
      const targetSubgroup = subgroups.find(s => s.id === targetId);
      
      if (draggedSubgroup && targetSubgroup) {
        // Check if they have the same parent
        const draggedParentPath = draggedSubgroup.name.split(' > ').slice(0, -1).join(' > ');
        const targetParentPath = targetSubgroup.name.split(' > ').slice(0, -1).join(' > ');
        
        if (draggedParentPath === targetParentPath) {
          // They have the same parent, so reorder
          const sameParentSubgroups = subgroups.filter(s => {
            const parentPath = s.name.split(' > ').slice(0, -1).join(' > ');
            return parentPath === draggedParentPath;
          });
          
          const draggedIndex = sameParentSubgroups.findIndex(s => s.id === draggedId);
          const targetIndex = sameParentSubgroups.findIndex(s => s.id === targetId);
          
          if (draggedIndex !== -1 && targetIndex !== -1 && draggedIndex !== targetIndex) {
            const newSameParentSubgroups = [...sameParentSubgroups];
            const [draggedSub] = newSameParentSubgroups.splice(draggedIndex, 1);
            newSameParentSubgroups.splice(targetIndex, 0, draggedSub);
            
            // Rebuild full subgroups array with new order
            const otherSubgroups = subgroups.filter(s => {
              const parentPath = s.name.split(' > ').slice(0, -1).join(' > ');
              return parentPath !== draggedParentPath;
            });
            
            const newSubgroups = [...otherSubgroups, ...newSameParentSubgroups];
            onReorderSubgroups?.(newSubgroups);
            setDraggedItem(null);
            return;
          }
        } else {
          // Different parents - this will be handled later as a move operation
          // Let it fall through to the move subgroup logic
        }
      }
    }

    // Check if we're trying to create a circular reference (prevent dropping parent into child)
    const isCircularReference = (draggedId: string, targetId: string) => {
      if (draggedType === 'group' && targetType === 'subgroup') {
        const targetSubgroup = subgroups.find(s => s.id === targetId);
        if (targetSubgroup) {
          const draggedGroup = groups.find(g => g.id === draggedId);
          return targetSubgroup.name.startsWith(draggedGroup?.name + ' >');
        }
      }
      return false;
    };

    if (isCircularReference(draggedId, targetId)) {
      toast({
        title: "Invalid move",
        description: "Cannot move a group into its own subgroup.",
        variant: "destructive",
      });
      setDraggedItem(null);
      return;
    }

    if (draggedType === 'group' && targetType === 'group') {
      // Always convert group to subgroup when dropped on another group
      if (onConvertGroupToSubgroup) {
        onConvertGroupToSubgroup(draggedId, targetId);
        toast({
          title: "Group converted",
          description: "The group has been converted to a subgroup successfully.",
        });
      }
    } else if (draggedType === 'group' && targetType === 'subgroup') {
      // Convert group to subgroup of target subgroup
      if (onConvertGroupToSubgroup) {
        onConvertGroupToSubgroup(draggedId, targetId);
      }
    } else if (draggedType === 'subgroup' && targetType === 'all-notes') {
      // Convert subgroup back to group when dropped on "All Notes"
      if (onConvertSubgroupToGroup) {
        onConvertSubgroupToGroup(draggedId);
        toast({
          title: "Subgroup converted to group",
          description: "The subgroup has been converted to a main group successfully.",
        });
      }
    } else if (draggedType === 'subgroup' && (targetType === 'group' || targetType === 'subgroup')) {
      // Check if we're trying to create a circular reference (prevent dropping parent into child)
      const wouldCreateCircularReference = () => {
        if (targetType === 'subgroup') {
          const draggedSubgroup = subgroups.find(s => s.id === draggedId);
          const targetSubgroup = subgroups.find(s => s.id === targetId);
          if (draggedSubgroup && targetSubgroup) {
            // Prevent dropping a parent subgroup into its own child
            return targetSubgroup.name.startsWith(draggedSubgroup.name + ' >');
          }
        }
        return false;
      };

      if (wouldCreateCircularReference()) {
        toast({
          title: "Invalid move",
          description: "Cannot move a subgroup into its own child subgroup.",
          variant: "destructive",
        });
        setDraggedItem(null);
        return;
      }

      // Move subgroup to different parent (group or subgroup)
      if (onMoveSubgroup) {
        onMoveSubgroup(draggedId, targetId);
        const targetName = targetType === 'group' 
          ? groups.find(g => g.id === targetId)?.name 
          : getDisplayName(subgroups.find(s => s.id === targetId)?.name || '');
        toast({
          title: "Subgroup moved",
          description: `Subgroup moved to "${targetName}".`,
        });
      }
    }
    
    setDraggedItem(null);
  };

  const getSubgroupName = (groupId: string) => subgroupNames[groupId] || "";
  const setSubgroupName = (groupId: string, value: string) => {
    setSubgroupNames(prev => ({ ...prev, [groupId]: value }));
  };

  const isPopoverOpen = (groupId: string) => showSubgroupPopovers[groupId] || false;
  const setPopoverOpen = (groupId: string, open: boolean) => {
    setShowSubgroupPopovers(prev => ({ ...prev, [groupId]: open }));
  };

  // Extract the display name from the full path (e.g., "Group > Sub > SubSub" -> "SubSub")
  const getDisplayName = (fullName: string) => {
    const parts = fullName.split(' > ');
    return parts[parts.length - 1];
  };

  const getHierarchyLevel = (fullName: string) => {
    return (fullName.match(/>/g) || []).length;
  };

  const getHierarchyStyles = (level: number) => {
    const borderColors = ['border-l-blue-400', 'border-l-green-400', 'border-l-purple-400', 'border-l-orange-400'];
    const opacities = ['opacity-100', 'opacity-90', 'opacity-80', 'opacity-70'];
    
    return {
      borderClass: level > 0 ? `border-l-4 ${borderColors[(level - 1) % borderColors.length]}` : '',
      opacityClass: opacities[Math.min(level, opacities.length - 1)],
      paddingLeft: level > 0 ? 'pl-3' : '',
      marginLeft: `${level * 12}px`
    };
  };

  const getSubgroupsForGroup = (groupId: string) => {
    return subgroups.filter((subgroup) => {
      if (!selectedSubgroup) {
        // If no subgroup is selected, show only first level subgroups
        return (subgroup.name.match(/>/g) || []).length === 1 && 
               subgroup.name.startsWith(groups.find(g => g.id === groupId)?.name + ' > ');
      } else {
        // If a subgroup is selected, show only its direct children
        const selectedSubgroupName = subgroups.find(s => s.id === selectedSubgroup)?.name || '';
        const expectedPrefix = selectedSubgroupName + ' > ';
        return subgroup.name.startsWith(expectedPrefix) && 
               subgroup.name.slice(expectedPrefix.length).indexOf(' > ') === -1;
      }
    });
  };

  // Helper functions for tree view
  const getAllSubgroupsForGroup = (groupId: string) => {
    const groupName = groups.find(g => g.id === groupId)?.name;
    if (!groupName) return [];
    
    return subgroups
      .filter(subgroup => subgroup.name.startsWith(groupName + ' > '))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const hasChildren = (subgroupId: string) => {
    const subgroup = subgroups.find(s => s.id === subgroupId);
    if (!subgroup) return false;
    return subgroups.some(s => s.name.startsWith(subgroup.name + ' > '));
  };

  const toggleSubgroupExpansion = (subgroupId: string) => {
    setExpandedSubgroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(subgroupId)) {
        newSet.delete(subgroupId);
      } else {
        newSet.add(subgroupId);
      }
      return newSet;
    });
  };

  const isSubgroupVisible = (subgroup: Group) => {
    const pathParts = subgroup.name.split(' > ');
    if (pathParts.length === 2) return true; // Top level subgroups are always visible
    
    // Check if all parent subgroups are expanded
    for (let i = 2; i < pathParts.length; i++) {
      const parentPath = pathParts.slice(0, i).join(' > ');
      const parentSubgroup = subgroups.find(s => s.name === parentPath);
      if (parentSubgroup && !expandedSubgroups.has(parentSubgroup.id)) {
        return false;
      }
    }
    return true;
  };

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Groups</h2>
        <Button onClick={onAddGroup} data-tour="add-group">
          <Plus className="h-4 w-4 mr-2" />
          Add Group
        </Button>
      </div>
      
      <div className="flex gap-2 flex-wrap mb-4">
        <div
          className={`transition-all duration-200 ${
            dragOverTarget === 'all-notes' ? 'scale-105 ring-2 ring-blue-400' : ''
          }`}
          onDragOver={handleDragOver}
          onDragEnter={(e) => handleDragEnter(e, 'all-notes')}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, 'all-notes', 'all-notes')}
          data-drop-target="all-notes"
          data-drop-type="all-notes"
        >
          <Button
            variant={selectedGroup === null ? "default" : "outline"}
            onClick={() => onGroupSelect(null)}
          >
            All Notes
          </Button>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {groups.map((group, index) => (
            <div
              key={group.id}
              className={`flex items-center gap-1 transition-all duration-200 ${
                dragOverTarget === group.id ? 'scale-105 ring-2 ring-blue-400' : ''
              }`}
              draggable
              onDragStart={(e) => handleDragStart(e, group.id, 'group')}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onDragEnter={(e) => handleDragEnter(e, group.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, group.id, 'group', index)}
              onTouchStart={(e) => handleTouchStart(e, group.id, 'group')}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              data-drop-target={group.id}
              data-drop-type="group"
              data-drop-index={index.toString()}
            >
              <div className="flex items-center gap-1">
                <Button
                  variant={selectedGroup === group.id ? "default" : "outline"}
                  onClick={() => onGroupSelect(group.id)}
                  className="select-none cursor-pointer"
                >
                  {group.name}
                </Button>
                {selectedGroup === group.id && (
                  <>
                    <Popover 
                      open={isPopoverOpen(group.id)} 
                      onOpenChange={(open) => setPopoverOpen(group.id, open)}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-blue-500 hover:text-blue-700 hover:bg-blue-100"
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80">
                        <div className="space-y-4">
                          <h4 className="font-medium">Create Subgroup</h4>
                          <Input
                            placeholder="Subgroup name..."
                            value={getSubgroupName(group.id)}
                            onChange={(e) => setSubgroupName(group.id, e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleCreateSubgroup(group.id)}
                          />
                          <div className="flex gap-2">
                            <Button onClick={() => handleCreateSubgroup(group.id)} size="sm">
                              Save Subgroup
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setPopoverOpen(group.id, false)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteGroup(group.id)}
                      className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-100"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Show subgroups when a group is selected */}
      {selectedGroup && subgroups.length > 0 && (
        <div className="mt-4">
          {/* Back button when a subgroup is selected */}
          {selectedSubgroup && (
            <div className="mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const currentSubgroup = subgroups.find(s => s.id === selectedSubgroup);
                  if (currentSubgroup) {
                    const currentPath = currentSubgroup.name;
                    const pathParts = currentPath.split(' > ');
                    if (pathParts.length > 2) {
                      // Navigate to direct parent subgroup
                      const parentPath = pathParts.slice(0, -1).join(' > ');
                      const parentSubgroup = subgroups.find(s => s.name === parentPath);
                      if (parentSubgroup) {
                        onSubgroupSelect?.(parentSubgroup.id);
                      }
                    } else {
                      // Navigate back to parent group
                      onSubgroupSelect?.(null);
                    }
                  }
                }}
                className="text-sm"
              >
                ← Back to {(() => {
                  const currentSubgroup = subgroups.find(s => s.id === selectedSubgroup);
                  if (currentSubgroup) {
                    const pathParts = currentSubgroup.name.split(' > ');
                    if (pathParts.length > 2) {
                      return pathParts[pathParts.length - 2];
                    }
                  }
                  return groups.find(g => g.id === selectedGroup)?.name;
                })()}
              </Button>
            </div>
          )}
           
          {/* Tree View Toggle */}
          {getAllSubgroupsForGroup(selectedGroup).length > 0 && (
            <div className="mb-3 flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setTreeViewMode(!treeViewMode)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                <List className="h-3 w-3 mr-1" />
                {treeViewMode ? 'Simple View' : 'Tree View'}
              </Button>
              {treeViewMode && (
                <span className="text-xs text-muted-foreground">
                  Click arrows to expand/collapse
                </span>
              )}
            </div>
          )}

          {/* Show selected subgroup prominently with actions */}
          {selectedSubgroup && !treeViewMode && (
            <div className="mb-3">
              <div className="flex items-center gap-1">
                <Button
                  variant="default"
                  size="sm"
                  className="text-sm font-medium"
                  title={subgroups.find(s => s.id === selectedSubgroup)?.name}
                >
                  {getDisplayName(subgroups.find(s => s.id === selectedSubgroup)?.name || '')}
                </Button>
                <Popover 
                  open={isPopoverOpen(selectedSubgroup)} 
                  onOpenChange={(open) => setPopoverOpen(selectedSubgroup, open)}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-blue-500 hover:text-blue-700 hover:bg-blue-100"
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-4">
                      <h4 className="font-medium">Create Subgroup</h4>
                      <Input
                        placeholder="Subgroup name..."
                        value={getSubgroupName(selectedSubgroup)}
                        onChange={(e) => setSubgroupName(selectedSubgroup, e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCreateSubgroup(selectedSubgroup)}
                      />
                      <div className="flex gap-2">
                        <Button onClick={() => handleCreateSubgroup(selectedSubgroup)} size="sm">
                          Save Subgroup
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setPopoverOpen(selectedSubgroup, false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteGroup(selectedSubgroup)}
                  className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-100"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Tree View Mode */}
          {treeViewMode ? (
            <div className="space-y-1">
              {getAllSubgroupsForGroup(selectedGroup)
                .filter(subgroup => isSubgroupVisible(subgroup))
                .map((subgroup) => {
                  const hierarchyLevel = getHierarchyLevel(subgroup.name);
                  const styles = getHierarchyStyles(hierarchyLevel);
                  const hasChildrenFlag = hasChildren(subgroup.id);
                  const isExpanded = expandedSubgroups.has(subgroup.id);
                  
                  return (
                    <div key={subgroup.id} className="flex items-center gap-1">
                      <div 
                        style={{ width: `${hierarchyLevel * 20}px` }}
                        className="flex-shrink-0"
                      />
                      
                      {hasChildrenFlag && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 p-0"
                          onClick={() => toggleSubgroupExpansion(subgroup.id)}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-3 w-3" />
                          ) : (
                            <ChevronRight className="h-3 w-3" />
                          )}
                        </Button>
                      )}
                      
                      {!hasChildrenFlag && <div className="w-6" />}
                      
                      <div
                        className={`flex items-center gap-1 transition-all duration-200 ${
                          dragOverTarget === subgroup.id && draggedItem?.type === 'subgroup' && draggedItem?.id !== subgroup.id 
                            ? 'scale-105 ring-2 ring-green-400 bg-green-50' 
                            : dragOverTarget === subgroup.id 
                              ? 'scale-105 ring-2 ring-blue-400' 
                              : ''
                        }`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, subgroup.id, 'subgroup')}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => handleDragEnter(e, subgroup.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, subgroup.id, 'subgroup')}
                        onTouchStart={(e) => handleTouchStart(e, subgroup.id, 'subgroup')}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        data-drop-target={subgroup.id}
                        data-drop-type="subgroup"
                      >
                        <Button
                          variant={selectedSubgroup === subgroup.id ? "default" : "outline"}
                          size="sm"
                          onClick={() => onSubgroupSelect?.(subgroup.id)}
                          className={`text-sm ${styles.borderClass} ${styles.opacityClass} transition-all duration-200 select-none cursor-pointer`}
                          title={subgroup.name}
                        >
                          {getDisplayName(subgroup.name)}
                          {hasChildrenFlag && (
                            <span className="ml-1 text-xs opacity-60">
                              ({subgroups.filter(s => s.name.startsWith(subgroup.name + ' > ')).length})
                            </span>
                          )}
                        </Button>
                        
                        {selectedSubgroup === subgroup.id && (
                          <div className="flex items-center gap-1 ml-1">
                            <Popover 
                              open={isPopoverOpen(subgroup.id)} 
                              onOpenChange={(open) => setPopoverOpen(subgroup.id, open)}
                            >
                              <PopoverTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-blue-500 hover:text-blue-700 hover:bg-blue-100"
                                >
                                  <Plus className="h-3 w-3" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80">
                                <div className="space-y-4">
                                  <h4 className="font-medium">Create Subgroup</h4>
                                  <Input
                                    placeholder="Subgroup name..."
                                    value={getSubgroupName(subgroup.id)}
                                    onChange={(e) => setSubgroupName(subgroup.id, e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleCreateSubgroup(subgroup.id)}
                                  />
                                  <div className="flex gap-2">
                                    <Button onClick={() => handleCreateSubgroup(subgroup.id)} size="sm">
                                      Save Subgroup
                                    </Button>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => setPopoverOpen(subgroup.id, false)}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteGroup(subgroup.id)}
                              className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-100"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            /* Regular View Mode */
            groups.map((group) => {
              if (group.id !== selectedGroup) return null;
              
              const groupSubgroups = getSubgroupsForGroup(group.id);
              
              return (
                 <div
                  key={group.id}
                  className={`flex gap-2 flex-wrap min-h-12 p-2 rounded-lg border-2 border-dashed transition-colors ${
                    dragOverTarget === group.id ? 'border-blue-400 bg-blue-50' : 'border-transparent'
                  }`}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, group.id)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, group.id, 'group')}
                  data-drop-target={group.id}
                  data-drop-type="group"
                >
                  {groupSubgroups
                    .sort((a, b) => {
                      const levelA = getHierarchyLevel(a.name);
                      const levelB = getHierarchyLevel(b.name);
                      if (levelA !== levelB) return levelA - levelB;
                      return a.name.localeCompare(b.name);
                    })
                    .map((subgroup, index) => {
                      const hierarchyLevel = getHierarchyLevel(subgroup.name);
                      const styles = getHierarchyStyles(hierarchyLevel);
                    
                      return (
                        <div key={subgroup.id} className="relative">
                          <div
                            className={`flex items-center gap-1 transition-all duration-200 ${
                              dragOverTarget === subgroup.id && draggedItem?.type === 'subgroup' && draggedItem?.id !== subgroup.id 
                                ? 'scale-105 ring-2 ring-green-400 bg-green-50' 
                                : dragOverTarget === subgroup.id 
                                  ? 'scale-105 ring-2 ring-blue-400' 
                                  : ''
                            }`}
                            style={{ marginLeft: styles.marginLeft }}
                            draggable
                            onDragStart={(e) => handleDragStart(e, subgroup.id, 'subgroup')}
                            onDragEnd={handleDragEnd}
                            onDragOver={handleDragOver}
                            onDragEnter={(e) => handleDragEnter(e, subgroup.id)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, subgroup.id, 'subgroup')}
                            onTouchStart={(e) => handleTouchStart(e, subgroup.id, 'subgroup')}
                            onTouchMove={handleTouchMove}
                            onTouchEnd={handleTouchEnd}
                            data-drop-target={subgroup.id}
                            data-drop-type="subgroup"
                            data-drop-index={index.toString()}
                          >
                            <Button
                              variant={selectedSubgroup === subgroup.id ? "default" : "outline"}
                              size="sm"
                              onClick={() => onSubgroupSelect?.(subgroup.id)}
                              className={`text-sm ${styles.borderClass} ${styles.paddingLeft} ${styles.opacityClass} transition-all duration-200 select-none cursor-pointer`}
                              title={subgroup.name}
                            >
                              {getDisplayName(subgroup.name)}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default GroupManager;